import React from 'react';
import PropTypes from 'prop-types';

// External components
import Measure from 'react-measure';
import RegionSelect from 'react-region-select';

// Common components
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import LoadingMessage from '../../common/LoadingMessage/LoadingMessage';

// Services
import AppConfiguration from '../../../services/AppConfiguration';
import CropAreaService from '../../../services/CropAreaService';

// Component styles
import './cropArea.scss';

// Constants
import appErrorMessages from '../../../constants/appErrors';

/**
 * This component shows the crop area for the uploaded image.
 * - Get the crop image data from the BE and save it in the store.
 * - Render the image when the BE call is done.
 * @extends {Component}
 */
class CropArea extends React.Component {
  constructor(props) {
    super(props);

    /**
     * Instance of the CropAreaService service.
     * @type {CropAreaService}
     */
    this.cropAreaService = new CropAreaService();

    /**
     * Instance of the App configuration data.
     * @type {Object}
     */
    this.appConfig = AppConfiguration.getConfig();

    /**
     * Several styles that customize each one of the crop regions in the
     * RegionSelect component.
     * @type {Object}
     */
    this.cropRegionStyle = {
      backgroundImage: `linear-gradient(to right, rgba(0,0,0,.5) 50%, rgba(255,255,255,.5) 50%),
                        linear-gradient(to right, rgba(0,0,0,.5) 50%, rgba(255,255,255,.5) 50%),
                        linear-gradient(to bottom, rgba(0,0,0,.5) 50%, rgba(255,255,255,.5) 50%),
                        linear-gradient(to bottom, rgba(0,0,0,.5) 50%, rgba(255,255,255,.5) 50%)`,
      backgroundPosition: 'left top, left bottom, left top, right top',
      backgroundRepeat: 'repeat-x, repeat-x, repeat-y, repeat-y',
      backgroundSize: '20px 3px, 20px 3px, 3px 20px, 3px 20px',
      border: 'none',
      outline: 'none',
    };

    /**
     * It is used for positioning the icon below the crop region and also for checking if the container image hides
     * the trash icon.
     * @type {Number}
     */
    this.iconBottomPosition = 39;

    /**
     * @type {HTMLElement}
     */
    [this.bodyElement] = document.getElementsByTagName('body');

    /**
     * Component initial state.
     * @type {Object}
     * @property {Object|Null} regionSelectStyle  It defines the dynamic styles for the region select component
     *                                            as the width and the height that will vary depending the resize
     *                                            of the image. These values need to be setted for the overflow
     *                                            property to work.
     */
    this.state = {
      regionSelectStyle: null,
    };

    // Context binding for functions.
    this.onImageResize = this.onImageResize.bind(this);
    this.renderTrashIcon = this.renderTrashIcon.bind(this);
    this.isTrashIconHidden = this.isTrashIconHidden.bind(this);
    this.onCropAreaTouchEnd = this.onCropAreaTouchEnd.bind(this);
    this.onCropAreaTouchStart = this.onCropAreaTouchStart.bind(this);
  }

  /**
   * Callback for when the image gets resized for some reason.
   * It receives a DOMRectReadOnly object with position and dimension data. This method
   * extracts height and width and updates the regionSelectStyle state. It also executes
   * the onImageResize callback with the dimensionData.
   * @param {Object}
   * @property {Object} dimensionData
   */
  onImageResize(dimensionData) {
    const { width, height } = dimensionData.entry;

    this.props.onImageResize(dimensionData);
    this.setState({
      regionSelectStyle: {
        width,
        height,
      },
    });
  }

  /**
   * Callback for when the touch event starts. This callback will be executed only in mobile browsers.
   */
  onCropAreaTouchStart() {
    this.bodyElement.style.overflow = 'hidden';
  }

  /**
   * Callback for when the touch event ends. This callback will be executed only in mobile browsers.
   */
  onCropAreaTouchEnd() {
    this.bodyElement.style.overflow = 'auto';
  }

  /**
   * @return The Crop Area element className with the `-loading` modifier when the loading prop is defined.
   */
  getImageClassName() {
    const isLoading = this.props.loading;
    const isDrawMode = this.props.drawingMode;
    const loadingClassModifier = isLoading ? '-loading' : '';
    const drawingModeClassModifier = isDrawMode ? '-drawing' : '';
    const classNameModifiers = `${loadingClassModifier} ${drawingModeClassModifier}`;
    return `wtfCropArea_image ${classNameModifiers}`;
  }

  /**
   * Checks if the trash icon is hidden by the bottom of the container image.
   * @param {Number} selectedCropRegion selected crop region position.
   * @return {Boolean}
   */
  isTrashIconHidden(selectedCropRegion) {
    const { cropAreaSize } = this.props;
    const cropRegion = this.props.cropRegions[selectedCropRegion];
    const cropRegionPixelData =
      this.cropAreaService.translatePercentagesToPixels(cropRegion, cropAreaSize.width, cropAreaSize.height);

    return cropRegionPixelData.y + cropRegionPixelData.height + this.iconBottomPosition > cropAreaSize.height;
  }

  /**
   * @return If errorCode is presented, render the error message component, otherwise null.
   */
  renderErrorMessage() {
    let dataToRender = null;

    if (this.props.errorCode) {
      const errorMessage = appErrorMessages.crop[this.props.errorCode] ?
        appErrorMessages.crop[this.props.errorCode] :
        appErrorMessages.crop.DEFAULT;

      dataToRender = (
        <div className='wtfCropArea_error'>
          <ErrorMessage text={errorMessage} onClose={this.props.onErrorClose} />
        </div>
      );
    }

    return dataToRender;
  }

  /**
   * @return Render Loading component while getting image crop data from the BE.
   */
  renderLoading() {
    const text = 'Hang tight while we find the text in your image...';

    return this.props.loading ? <LoadingMessage text={text} /> : null;
  }

  /**
   * @return Render Image if the image url exists.
   */
  renderImage() {
    let image = null;

    if (this.props.imageUrl) {
      image = (
        <Measure onResize={this.onImageResize}>
          {({ measureRef }) => (
            <img
              className={this.getImageClassName()}
              ref={measureRef}
              alt='Crop'
              src={this.props.imageUrl}
              onLoad={this.props.onImageLoaded}
            />
          )}
        </Measure>
      );
    }

    return image;
  }

  /**
   * This method renders an overlay for the image when the drawing mode is active. This allow the user
   * of drawing a new crop area over an existing one.
   * @return {ReactElement}
   */
  renderImageOverlay() {
    return this.props.drawingMode ? <div className='wtfCropArea_imageOverlay' /> : null;
  }

  /**
   * This method is called in every region creation and modification. It shows a trash icon
   * if the region is the one selected.
   * @param {Object} regionProps An object with the region props.
   * @return {ReactElement}
   */
  renderTrashIcon(regionProps) {
    let dataToRender = null;
    const { selectedCropRegion } = this.props;

    // Render a trash icon only for the selected region.
    if (regionProps.data && regionProps.index === selectedCropRegion) {
      const buttonStyle = {
        bottom: this.isTrashIconHidden(selectedCropRegion) ? 0 : `-${this.iconBottomPosition}px`,
      };

      dataToRender = (
        <button
          className='wtfCropArea_trashButton'
          onClick={() => this.props.onDelete(regionProps.index)}
          onKeyPress={() => this.props.onDelete(regionProps.index)}
          style={buttonStyle}
        >
          <img
            className='wtfCropArea_trashIcon'
            src={`${this.appConfig.cdnUrl.url}/icon_trash.svg`}
            alt='trash icon'
          />
        </button>
      );
    }

    return dataToRender;
  }

  render() {
    const { vendor, userAgent } = window.navigator;
    const safari = userAgent.toLowerCase().indexOf('safari') >= 0 && vendor.toLowerCase().indexOf('apple') >= 0;
    const safariClassName = safari ? '-safari' : '';

    return (
      <div
        onTouchStart={this.onCropAreaTouchStart}
        onTouchEnd={this.onCropAreaTouchEnd}
        className={`wtfCropArea ${this.props.loading ? '-loading' : ''}`}
      >
        <RegionSelect
          className={`wtfRegionSelect ${safariClassName}`}
          regions={this.props.cropRegions}
          onChange={this.props.onCropRegionChange}
          regionStyle={this.cropRegionStyle}
          style={this.state.regionSelectStyle}
          constraint
          regionRenderer={this.renderTrashIcon}
        >
          {this.renderImage()}
          {this.renderImageOverlay()}
        </RegionSelect>
        {this.renderErrorMessage()}
        {this.renderLoading()}
      </div>
    );
  }
}

/**
 * Set the CropArea component props default values.
 * @type {Object}
 * @property {Boolean}  drawingMode
 * @property {Function} onImageLoaded
 * @property {Function} onImageResize
 * @property {Function} onDelete
 */
CropArea.defaultProps = {
  drawingMode: false,
  onImageLoaded: () => {},
  onImageResize: () => {},
  onDelete: () => {},
};

/**
 * Declare the CropArea component PropTypes for better validation.
 * @type {Object}
 * @property {String}   imageUrl             Image url for the crop area.
 * @property {String}   errorCode            If exists, it matches the error dictionary and shows the
 *                                           error message component.
 * @property {Boolean}  loading              It defines whether the loading component should be displayed or not.
 * @property {Object[]} cropRegions          Array of objects representing areas from the RegionSelect component.
 * @property {Boolean}  drawingMode          Flag that tells if the crop area component is allowed to create crop
 *                                           zones or not.
 * @property {Object}   cropAreaSize         Width and height of the crop area zone. Required to position the trash
 *                                           icon inside the crop region when needed.
 * @property {Number}   selectedCropRegion   It is used for showing the trash icon for the selected crop region.
 * @property {Function} onCropRegionChange   This callback will be called when the RegionSelect component changes.
 * @property {Function} onErrorClose         This callback will be called when an error in the BE call is presented and
 *                                           displayed and then the error is closed.
 * @property {Function} onImageLoaded        This callback will be called when the image has successfully loaded.
 * @property {Function} onImageResize        Callback called whenever the container of the image gets resized because
 *                                           of the image it's wrapping.
 * @property {Function} onDelete             Callback called whenever one of the crop regions needs to be deleted.
 */
CropArea.propTypes = {
  imageUrl: PropTypes.string.isRequired,
  errorCode: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  cropRegions: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    }),
  })).isRequired,
  drawingMode: PropTypes.bool,
  cropAreaSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  selectedCropRegion: PropTypes.number.isRequired,
  onCropRegionChange: PropTypes.func.isRequired,
  onErrorClose: PropTypes.func.isRequired,
  onImageLoaded: PropTypes.func,
  onImageResize: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CropArea;



// WEBPACK FOOTER //
// ./src/components/Crop/CropArea/CropArea.js